import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange, MatSelect, MatSelectChange } from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { debounceTime } from 'rxjs/operators';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import { QuestionnaireType } from '../../../../API';
import { Patient } from '../../../core/api/patient.service';
import {
  AccessState,
  QuestionnaireDeliveryMethod
} from '../../../core/schemas/subscription-plan.schema';
import { SubscriptionPlanAccessService } from '../../../core/services/subscription-plan-access.service';
import { PatientFormControlType } from '../../../shared/patient-form/patient-form.model';
import { CSVPatientsFormGroup } from '../import-wizard-file-received/csv-patients-form-group.model';
import {
  ImportWizardSendQuestionnairesService,
  SendQuestionnairesResult
} from './import-wizard-send-questionnaires.service';

class SendMethodsFormGroup extends FormGroup {
  controls: { byPhone: FormArray; byEmail: FormArray };

  constructor(fb: FormBuilder, initialByPhoneValues: boolean[], initialByEmailValues: boolean[]) {
    super({
      byPhone: fb.array(initialByPhoneValues),
      byEmail: fb.array(initialByEmailValues)
    });
  }
}

@Component({
  selector: 'csi-import-wizard-send-questionnaires',
  templateUrl: './import-wizard-send-questionnaires.component.html',
  styleUrls: ['./import-wizard-send-questionnaires.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportWizardSendQuestionnairesComponent implements OnInit, OnDestroy {
  @Input() csvPatientsFormGroup: CSVPatientsFormGroup;
  @Output() completed: EventEmitter<null> = new EventEmitter();
  @ViewChild('matSelect', { static: false }) masterMatSelect: MatSelect;

  public patientsToSendTo: Patient[];
  public readonly columnsToDisplay = [
    PatientFormControlType.FirstName,
    PatientFormControlType.LastName,
    PatientFormControlType.Email,
    PatientFormControlType.Phone,
    PatientFormControlType.DateOfBirth,
    'questionnaireType',
    'sendToPhone',
    'sendToEmail'
  ];
  protected typesToSend: FormArray;
  protected sendMethods: SendMethodsFormGroup;
  protected sendToPhoneIndeterminate = false;
  protected sendToEmailIndeterminate = false;
  public matSpinnerOptions: MatProgressButtonOptions;
  // Set after the send phase when one or more questionnaires could not be sent — shows exactly
  // which patients were missed instead of reporting blanket success.
  public sendFailureSummary: string = null;
  // Blocks a second send run: some questionnaires DID go out, so re-sending the batch would
  // deliver duplicates to those patients.
  private isSendRunComplete = false;
  protected readonly sendToPhoneControl = this.fb.control(false);
  protected readonly sendToEmailControl = this.fb.control(true);
  protected readonly QuestionnaireType = Object.keys(QuestionnaireType).map(
    key => QuestionnaireType[key]
  );

  constructor(
    private fb: FormBuilder,
    private importWizardSendQuestionnairesService: ImportWizardSendQuestionnairesService,
    private confirmModalService: ConfirmModalService,
    private translocoService: TranslocoService,
    private subscriptionPlanAccessService: SubscriptionPlanAccessService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // getRawValue, not .value: created rows are DISABLED by toApiInput(), and FormArray.value
    // excludes disabled children while any duplicate row stays enabled — .value would silently
    // drop exactly the just-created patients from the send list in mixed create/duplicate runs.
    this.patientsToSendTo = this.csvPatientsFormGroup.controls.patients.getRawValue() as Patient[];
    const isPhoneDeliveryEnabled = this.isDeliveryChannelEnabled(QuestionnaireDeliveryMethod.TEXT);
    this.typesToSend = this.fb.array([
      ...this.patientsToSendTo.map(() => QuestionnaireType.DEQ as string)
    ]);
    this.sendMethods = new SendMethodsFormGroup(
      this.fb,
      this.patientsToSendTo.map(() => false),
      this.patientsToSendTo.map(() => true)
    );
    if (!isPhoneDeliveryEnabled) {
      // The csiSubscriptionPlanAccessControl directive on the phone column only greys/badges the
      // cells — it never disables the child checkboxes — so without this, SMS stayed selectable
      // and sendable on plans without the TEXT add-on.
      this.sendMethods.controls.byPhone.disable();
      this.sendToPhoneControl.disable();
    }

    this.matSpinnerOptions = {
      active: false,
      text: '',
      raised: true,
      buttonColor: 'primary',
      spinnerSize: 19,
      mode: 'indeterminate'
    };
    this.updateSendCountText();

    // One debounced refresh per burst of checkbox writes — recomputing the questionnaire list on
    // every row's valueChanges froze the tab on very large imports.
    this.sendMethods.valueChanges
      .pipe(
        debounceTime(200),
        untilDestroyed(this)
      )
      .subscribe(() => this.updateSendCountText());
  }

  ngOnDestroy() {}

  private isDeliveryChannelEnabled(deliveryMethod: QuestionnaireDeliveryMethod): boolean {
    const deliveryMethodKey = 'questionnaireDeliveryMethods.' + deliveryMethod;
    const deliveryMethodAccess = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema[
      deliveryMethodKey
    ];
    if (!deliveryMethodAccess) {
      return true;
    }
    return deliveryMethodAccess.accessState === AccessState.ENABLED;
  }

  // The debounced refresh runs outside the change-detection pass that caused it, so under OnPush
  // it must markForCheck or the send-button label freezes at its previous count.
  private updateSendCountText() {
    const questionnairesToSendCount = this.numberOfQuestionnairesToSend;
    this.matSpinnerOptions.text =
      this.translocoService.translate('Send Questionnaires to') +
      ' ' +
      questionnairesToSendCount +
      ' ' +
      this.translocoService.translate('Patient' + (questionnairesToSendCount === 1 ? '' : 's'));
    this.changeDetectorRef.markForCheck();
  }

  // Counting must stay cheap — it runs after every debounced checkbox burst. Building the full
  // questionnaire inputs (one FormGroup per patient) just to take .length stalled the tab on
  // large imports; this count matches questionnaireInputs' filter exactly: a row is sent when
  // a checked method has a non-empty destination (email/phone).
  protected get numberOfQuestionnairesToSend(): number {
    const sendViaEmails = this.sendMethods.controls.byEmail.value as boolean[];
    const sendViaPhones = this.sendMethods.controls.byPhone.value as boolean[];
    return this.patientsToSendTo.filter(
      (patient, patientIndex) =>
        (sendViaEmails[patientIndex] && !!patient.email) ||
        (sendViaPhones[patientIndex] && !!patient.phone)
    ).length;
  }

  protected setAllQuestionnaireTypesTo(change: MatSelectChange) {
    this.masterMatSelect.value = undefined;
    // Silent per-row writes + one revalidation — same pattern as setAllSendMethodsTo below;
    // default setValue re-aggregates the whole FormArray per row, freezing the tab on ~10k rows.
    this.typesToSend.controls.forEach(questionnaireTypeControl =>
      questionnaireTypeControl.setValue(change.value, { onlySelf: true, emitEvent: false })
    );
    this.typesToSend.updateValueAndValidity();
  }

  protected setAllSendMethodsTo(change: MatCheckboxChange) {
    let targetControls: FormArray;
    if (change.source.name === 'phone') {
      targetControls = this.sendMethods.controls.byPhone;
    } else if (change.source.name === 'email') {
      targetControls = this.sendMethods.controls.byEmail;
    } else {
      return;
    }
    // Write every row silently, then revalidate once — one valueChanges emission per master
    // toggle instead of a full re-aggregation per row, which froze the tab on ~10k-row imports.
    targetControls.controls.forEach(sendMethodControl =>
      sendMethodControl.setValue(change.checked, { onlySelf: true, emitEvent: false })
    );
    targetControls.updateValueAndValidity();
  }

  protected updateMasterCheckboxes(checkboxType: 'phone' | 'email') {
    let formValues: boolean[], checkbox: FormControl;
    if (checkboxType === 'phone') {
      formValues = this.sendMethods.controls.byPhone.value;
      checkbox = this.sendToPhoneControl;
      this.sendToPhoneIndeterminate = formValues.some(val => !!val) && formValues.some(val => !val);
    } else {
      formValues = this.sendMethods.controls.byEmail.value;
      checkbox = this.sendToEmailControl;
      this.sendToEmailIndeterminate = formValues.some(val => !!val) && formValues.some(val => !val);
    }

    if (formValues.every(val => !!val)) {
      checkbox.setValue(true);
    } else if (formValues.every(val => !val)) {
      checkbox.setValue(false);
    }
  }

  public sendQuestionnaires() {
    if (this.isSendRunComplete) {
      return;
    }
    this.confirmModalService
      .show(
        `${this.translocoService.translate('Send Questionnaires')}`,
        `${this.translocoService.translate('You are about to send questionnaires to')} ${
          this.numberOfQuestionnairesToSend
        } ${this.translocoService.translate('patient(s). Would you like to continue?')}`,
        `${this.translocoService.translate('Send')}`,
        `${this.translocoService.translate('Cancel')}`
      )
      .subscribe(send => {
        if (!send) {
          return;
        }
        this.disableAllButtons();
        this.importWizardSendQuestionnairesService
          .sendQuestionnaires(
            this.patientsToSendTo,
            this.typesToSend.value,
            this.sendMethods.controls.byEmail.value,
            this.sendMethods.controls.byPhone.value
          )
          .pipe(untilDestroyed(this))
          .subscribe((sendResult: SendQuestionnairesResult) => {
            this.isSendRunComplete = true;
            if (sendResult.failures.length > 0) {
              // Some questionnaires were not sent. Do NOT report blanket success — show exactly
              // who was missed and why; the user leaves via Skip once they have read it. The
              // send button stays inert (guard above) so a re-click cannot double-send to the
              // patients whose questionnaires DID go out.
              this.sendFailureSummary = this.buildSendFailureSummary(sendResult);
              this.matSpinnerOptions.active = false;
              this.changeDetectorRef.markForCheck();
              return;
            }
            this.completed.emit(null);
          });
      });
  }

  private buildSendFailureSummary(sendResult: SendQuestionnairesResult): string {
    const sentCount = sendResult.sent.length;
    const totalCount = sentCount + sendResult.failures.length;
    const failedNames = sendResult.failures.map(failure => failure.name).join(', ');
    const firstReason = sendResult.failures[0] ? sendResult.failures[0].reason : '';
    return (
      sentCount +
      ' ' +
      this.translocoService.translate('of') +
      ' ' +
      totalCount +
      ' ' +
      this.translocoService.translate('questionnaires were sent') +
      '. ' +
      sendResult.failures.length +
      ' ' +
      this.translocoService.translate('could not be sent') +
      ': ' +
      failedNames +
      (firstReason ? ' — ' + firstReason : '') +
      '. ' +
      this.translocoService.translate("You can send these later from each patient's file") +
      '.'
    );
  }

  private disableAllButtons() {
    this.matSpinnerOptions.active = true;
    this.sendMethods.disable();
    this.typesToSend.disable();
    this.sendToEmailControl.disable();
    this.sendToPhoneControl.disable();
  }
}
